import React, { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import styles from './VehiclesMileageFiltersScreen.module.scss';
import Button from '../../../common/components/button/Button';
import FormItem from '../../../common/components/formItem/FormItem';
import Label from '../../../common/components/label/Label';
import { Col, Row } from 'react-flexbox-grid';
import { SelectValueLabel } from '../../../common/types/SelectValueLabel';
import Select from 'common/components/select/Select';
import { Controller, useForm } from 'react-hook-form';
import VehicleSegmentsService from 'api/vehicleSegments/VehicleSegmentsService';
import LocalsService from 'api/locals/LocalsService';
import Input from 'common/components/input/Input';
import RangeInputPicker from 'common/components/rangeInputPicker/RangeInputPicker';
import UseContractsService from 'api/contracts/useContract/UseContractsService';
import Utils from 'common/services/Utils';
import ToggleSwitch from 'common/components/toggleSwitch/ToggleSwitch';
import { DATEPICKER_FORMAT_DEFAULT, USE_CONTRACT_TYPES } from 'Config';

export interface Filters {
    registrationNumber?: string;
    registrationDate?: Date | null;
    segmentId?: string;
    segmentName?: string;
    localIds?: string[];
    localNames?: string[];
    localOperator?: string;
    driverId?: string;
    driverName?: string;
    useContractTypeId?: string;
    useContractTypeName?: string;
    useContractActive?: boolean;
    useContractEndsBefore?: Date | null;
    useContractEndsAfter?: Date | null;
    inactives?: boolean;
    page?: number;
    pathStartDate?: Date | null;
    pathEndDate?: Date | null;
    orderColumn?: string;
    orderBy?: string;
    scrollToItemId?: string;
}

type Props = {
    filters: Filters;
    onChange: (filters: Filters) => void;
    onFilter: (filters: Filters) => void;
    showTrajectDatesFilter?: boolean;
}

const VehiclesMileageFiltersScreen: React.FC<Props> = ({ filters, onChange, onFilter, showTrajectDatesFilter }: Props) => {
    const { t } = useTranslation();
    const guidEmpty = '00000000-0000-0000-0000-000000000000';
    const form = useForm<Filters>({ shouldUnregister: false, shouldFocusError: true, defaultValues: filters });
    const { getValues, setValue, reset, watch, control } = form;
    const [segmentsOptions, setSegments] = useState<SelectValueLabel[]>([]);
    const [localsOptions, setLocals] = useState<SelectValueLabel[]>([]);
    const [useContractTypeOptions, setUseContractTypeOptions] = useState<SelectValueLabel[]>([]);
    const [selectUseContractActive, setSelectUseContractActive] = useState<boolean | undefined>();
    const [selectUseContractTypeId, setSelectUseContractTypeId] = useState<string | undefined>();
    const [useContractTypePurchaseId, setUseContractTypePurchaseId] = useState<string>('');

    //const [driversOptions, setDrivers] = useState<SelectValueLabel[]>([]);

    const getData = async () => {
        const segmentsOptionsDB = await VehicleSegmentsService.catalog();
        setSegments(Utils.sortAlphabetically(segmentsOptionsDB));

        const localsOptionsDB = await LocalsService.catalog();
        setLocals([{ value: '00000000-0000-0000-0000-000000000000', label: t('maintenances.list.none') as string }, ...Utils.sortAlphabetically(localsOptionsDB)]);

        const useContractTypesOrig = await UseContractsService.getUseContractTypes();
        const useContractTypes = useContractTypesOrig.map(x => ({ ...x, label: t(`common.use_contract_types.${x.label}` as any) }));
        const useContractTypesOrdered = Utils.sortAlphabetically(useContractTypes);
        const ___useContractTypesOrdered = [...useContractTypesOrdered, { value: '00000000-0000-0000-0000-000000000000', label: t('contracts.use_contract.without_contract') }];
        setUseContractTypeOptions(___useContractTypesOrdered);
        setUseContractTypePurchaseId(useContractTypesOrig.find(x => x.label == USE_CONTRACT_TYPES.PURCHASE)?.value ?? '')

        //const driversOptions = await DriversService.catalog();
        //setDrivers(driversOptions);
    }

    useEffect(() => {

        if (form.getValues('useContractActive') != undefined) {
            setSelectUseContractActive(form.getValues('useContractActive'));
        }

        if (form.getValues('useContractTypeId') != undefined) {
            setSelectUseContractTypeId(form.getValues('useContractTypeId'));
        }



        getData();
    }, []);

    const clearFilters = () => {
        reset({
            registrationNumber: undefined,
            registrationDate: undefined,
            segmentId: undefined,
            localIds: [],
            localNames: [],
            localOperator: 'OR',
            useContractTypeId: undefined,
            useContractActive: undefined,
            useContractEndsAfter: undefined,
            useContractEndsBefore: undefined,
            pathStartDate: undefined,
            pathEndDate: undefined,
            inactives: false,
            orderColumn: undefined,
            orderBy: undefined,
        });

        // Reset local state
        setSelectUseContractActive(undefined);
        setSelectUseContractTypeId(undefined);

        onInputChange();
        onSubmit();
    }

    const onSubmit = () => {
        onFilter(getValues());
    }

    const onInputChange = () => {
        onChange(getValues());
    }

    const onSelectUseContractActive = (status: boolean) => {
        if (watch('useContractTypeId') == useContractTypePurchaseId) {
            return;
        }

        setSelectUseContractActive(selectUseContractActive == status ? undefined : status);
        setValue('useContractActive', selectUseContractActive == status ? undefined : status);


        //onInputChange();
    }


    return (
        <form onSubmit={form.handleSubmit(onSubmit)}>
            <div className={styles.formContent}>
                <div>
                    <FormItem>
                        <Row>
                            <Col xs={6}>
                                <FormItem>
                                    <Label className={styles.bold}>{t('vehicle.registration_identification')}</Label>
                                    <Input
                                        maxLength={255}
                                        name="registrationNumber"
                                        onKeyUp={e => {
                                            setValue('registrationNumber', e.currentTarget.value != '' ? e.currentTarget.value : null);
                                            onInputChange();
                                        }}
                                        placeholder={t('vehicle.registration_identification')}
                                        autoComplete='off'
                                        defaultValue={watch('registrationNumber')} />
                                </FormItem>
                            </Col>
                            <Col xs={6}>
                                <FormItem>
                                    <Label className={styles.bold}>{t('vehicle.type')}</Label>
                                    <Controller
                                        render={({ onChange: onSelectChange, value }) => {
                                            return (
                                                <Select
                                                    options={segmentsOptions}
                                                    isClearable
                                                    placeholder={t('vehicle.type')}
                                                    onChange={(data: SelectValueLabel) => {
                                                        onSelectChange(data?.value ?? null);
                                                        setValue('segmentName', data?.label ?? null);
                                                        onInputChange();
                                                    }}
                                                    value={segmentsOptions.find(x => x.value === value) ? { label: segmentsOptions.find(x => x.value === value)?.label ?? '', value: value } : null}
                                                />
                                            );
                                        }}
                                        control={control}
                                        name="segmentId"
                                        defaultValue={getValues('segmentId')} />
                                </FormItem>
                            </Col>
                        </Row>

                        <Row>
                            <Col xs={6}>
                                <FormItem>
                                    <div className={styles.labelWithOperator}>
                                        <Label className={styles.bold}>{t('vehicle.local')}</Label>
                                        <div className={styles.operatorToggle}>
                                            <span title={t('common.filter_all_tooltip' as any) as string}>
                                                <Button
                                                    text={t('common.filter_all' as any) as string}
                                                    className={styles.firstChild}
                                                    preset={watch('localOperator') === 'AND' ? 'primary' : 'secondary'}
                                                    onClick={() => { setValue('localOperator', 'AND'); onInputChange(); }}
                                                    size={'small'}
                                                />
                                            </span>
                                            <span title={t('common.filter_any_tooltip' as any) as string}>
                                                <Button
                                                    text={t('common.filter_any' as any) as string}
                                                    className={styles.lastChild}
                                                    preset={watch('localOperator') !== 'AND' ? 'primary' : 'secondary'}
                                                    onClick={() => { setValue('localOperator', 'OR'); onInputChange(); }}
                                                    size={'small'}
                                                />
                                            </span>
                                        </div>
                                    </div>
                                    <Controller
                                        render={({ onChange: onSelectChange, value }) => (
                                            <Select
                                                options={localsOptions}
                                                isMulti
                                                isClearable
                                                placeholder={t('vehicle.local')}
                                                onChange={(data: SelectValueLabel[]) => {
                                                    onSelectChange(data?.map(d => d.value) ?? []);
                                                    setValue('localNames', data?.map(d => d.label) ?? []);
                                                    onInputChange();
                                                }}
                                                value={localsOptions.filter(x => ((value as string[]) ?? []).includes(x.value))}
                                            />
                                        )}
                                        control={control}
                                        name="localIds"
                                        defaultValue={getValues('localIds') || []}
                                    />
                                </FormItem>
                            </Col>
                        </Row>

                        <Row>
                            <Col xs={6}>
                                <FormItem>
                                    <Label className={styles.bold}>{t('common.type_funding')}</Label>
                                    <Controller
                                        render={({ onChange: onSelectChange, value }) => {
                                            return (
                                                <Select
                                                    options={useContractTypeOptions}
                                                    isClearable
                                                    placeholder={t('common.type_funding')}
                                                    onChange={(data: SelectValueLabel) => {
                                                        onSelectChange(data?.value ?? null);
                                                        setValue('useContractTypeId', data?.value ?? null);
                                                        setValue('useContractTypeName', data?.label ?? null);
                                                        setSelectUseContractTypeId(data?.value ?? null);

                                                        if (data?.value == guidEmpty) {
                                                            setValue('useContractActive', undefined);
                                                            setValue('useContractEndsAfter', undefined);
                                                            setValue('useContractEndsBefore', undefined);
                                                        }


                                                        if (data && data.value == useContractTypePurchaseId) {
                                                            setSelectUseContractActive(undefined);
                                                            setValue('useContractActive', undefined);
                                                            form.setValue('useContractEndsAfter', undefined);
                                                            form.setValue('useContractEndsBefore', undefined);
                                                        }
                                                        onInputChange();
                                                    }}
                                                    value={useContractTypeOptions.find(x => x.value === value) ? { label: useContractTypeOptions.find(x => x.value === value)?.label ?? '', value: value } : null}
                                                />
                                            );
                                        }}
                                        control={control}
                                        name="useContractTypeId"
                                        defaultValue={getValues('useContractTypeId')} />
                                </FormItem>
                            </Col>

                            {selectUseContractTypeId != guidEmpty && <Col xs={6}>
                                <FormItem>
                                    <Label className={styles.bold}>{t('contracts.use_contract.contract_state')}</Label>
                                    <div className={styles.useContractStatus} style={{ cursor: watch('useContractTypeId') == useContractTypePurchaseId ? 'not-allowed' : 'pointer' }}>
                                        <div onClick={() => onSelectUseContractActive(true)} style={{ width: '50%' }}>
                                            <div className={styles.labelContainer}>
                                                <div className={watch('useContractTypeId') == useContractTypePurchaseId ? styles.disabled : selectUseContractActive ? styles.selected : styles.notSelected}>
                                                    <span>{t('contracts.states.in_progress')}</span>
                                                </div>
                                            </div>
                                        </div>
                                        <div onClick={() => onSelectUseContractActive(false)} style={{ width: '50%' }}>
                                            <div className={styles.labelContainer}>
                                                <div className={watch('useContractTypeId') == useContractTypePurchaseId ? styles.disabled : selectUseContractActive != undefined && !selectUseContractActive ? styles.selected : styles.notSelected}>
                                                    <span>{t('contracts.states.expired')}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                </FormItem>
                            </Col>}

                        </Row>


                        <Row>
                            <Col xs={12}>
                                <FormItem>
                                    <RangeInputPicker
                                        label={t('contracts.use_contract.expiration_date')}
                                        onChange={(startValue: any, endValue: any) => {
                                            form.setValue('useContractEndsAfter', startValue ? moment(startValue).toDate() : undefined);
                                            form.setValue('useContractEndsBefore', endValue ? moment(endValue).toDate() : undefined);
                                            onInputChange();
                                        }}
                                        defaultStartValue={form.getValues('useContractEndsAfter')}
                                        defaultEndValue={form.getValues('useContractEndsBefore')}
                                        isDatePicker
                                        disabled={watch('useContractTypeId') == useContractTypePurchaseId}
                                    />
                                </FormItem>
                            </Col>
                        </Row>


                        {showTrajectDatesFilter && <Row>
                            <Col xs={12}>
                                <FormItem>
                                    <RangeInputPicker
                                        label={t('vehicles_mileage.list.path_date')}
                                        tooltip={t('vehicles_mileage.list.path_date_tooltip')}
                                        onChange={(startValue: any, endValue: any) => {
                                            form.setValue('pathStartDate', startValue ? moment(startValue).toDate() : undefined);
                                            form.setValue('pathEndDate', endValue ? moment(endValue).toDate() : undefined);
                                            onInputChange();
                                        }}
                                        defaultStartValue={form.getValues('pathStartDate')}
                                        defaultEndValue={form.getValues('pathEndDate')}
                                        dateFormat={DATEPICKER_FORMAT_DEFAULT}
                                        isDatePicker
                                    />
                                </FormItem>
                            </Col>
                        </Row>}
                        <Row>
                            <Col sm={6} md={6}>
                                <FormItem>
                                    <Label className={styles.bold}>{t('vehicle.inactives')}</Label>
                                    <ToggleSwitch
                                        name="inactives"
                                        form={form}
                                    />
                                </FormItem>
                            </Col>                    
                        </Row>
                    </FormItem>
                </div>

                <div className={styles.buttonsFooter}>
                    <FormItem>
                        <Button
                            text={t('common.clear')}
                            size={'normal'}
                            preset={'secondary'}
                            onClick={clearFilters}
                            type='reset' />
                        <Button
                            type='submit'
                            text={t('common.apply')}
                            size={'normal'}
                        />
                    </FormItem>
                </div>
            </div>
        </form>
    );
}

export default memo(VehiclesMileageFiltersScreen);
